import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogTitle, DialogFooter } from '../../../../components/ui/Dialog';
import type { EmployeeConflicts } from '../scheduleSaveError';

export type ScheduleSaveErrorDetails = {
  summary?: string;
  descriptions?: string[];
  employees?: EmployeeConflicts[];
};

type Props = {
  open: boolean;
  details: ScheduleSaveErrorDetails | null;
  onClose: () => void;
};

/**
 * Why a schedule save was refused: the sentence, then each employee with their other schedule and its times (CO-996).
 * One dialog for the drawer and the Recurring Schedules page, so both give HR the same guidance.
 */
export function ScheduleSaveErrorDialog({ open, details, onClose }: Props) {
  const { t } = useTranslation();
  const employees = details?.employees ?? [];
  const descriptions = details?.descriptions ?? [];
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>{t('CouldntSaveSchedule')}</DialogTitle></DialogHeader>
        <DialogBody className="max-h-[60vh] overflow-y-auto">
          <div className="space-y-3">
            <div className="text-sm text-foreground">
              {details?.summary || t('UnexpectedErrorOccurred')}
            </div>

            {/* The server's own sentences are English: shown only when there is no conflict list to say it better. */}
            {employees.length === 0 && descriptions.length > 1 && (
              <div className="text-sm text-muted-foreground">
                <div className="font-medium mb-1">{t('ScheduleDetails')}</div>
                <ul className="list-disc space-y-1" style={{ paddingInlineStart: '1.25rem' }}>
                  {descriptions.slice(0, 6).map((d, idx) => (
                    <li key={idx}>{d}</li>
                  ))}
                </ul>
              </div>
            )}

            {employees.length > 0 && (
              <ul className="divide-y divide-border rounded border border-border">
                {employees.slice(0, 5).map((employee) => {
                  const conflictCount = employee.rows.length + employee.dailyLimitDates.length;
                  return (
                    <li key={employee.key} className="px-3 py-2.5">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
                        {/* Never the user id: it is an internal system key (owner rule 2026-10-28). */}
                        <span className="font-medium text-foreground">{employee.name || t('people.formerEmployee')}</span>
                        {conflictCount > 0 && (
                          <span className="text-xs text-muted-foreground">{t('ConflictsCount', { count: conflictCount })}</span>
                        )}
                      </div>
                      <ul className="mt-1.5 space-y-1 text-xs">
                        {employee.rows.slice(0, 3).map((row) => (
                          <li key={row.key} className="flex flex-wrap justify-between gap-x-3">
                            {row.isOtherShift && (
                              <span className="text-foreground">{row.scheduleName || t('ScheduleConflictOneOffShift')}</span>
                            )}
                            {/* Isolated LTR so an Arabic page keeps the date, the times and the dash in reading order. */}
                            <bdi dir="ltr" className="tabular-nums text-muted-foreground">{row.range}</bdi>
                          </li>
                        ))}
                        {employee.dailyLimitDates.length > 0 && (
                          <li className="text-foreground">
                            {t('ScheduleConflictDailyLimit', { dates: employee.dailyLimitDates.slice(0, 3).join(', ') })}
                          </li>
                        )}
                        {employee.rows.length > 3 && (
                          <li className="text-muted-foreground">
                            {t('ScheduleConflictMoreShifts', { count: employee.rows.length - 3 })}
                          </li>
                        )}
                      </ul>
                    </li>
                  );
                })}
                {employees.length > 5 && (
                  <li className="px-3 py-2 text-xs text-muted-foreground">
                    {t('ScheduleConflictMoreEmployees', { count: employees.length - 5 })}
                  </li>
                )}
              </ul>
            )}

            {descriptions.length === 0 && employees.length === 0 && (
              <div className="text-xs text-muted-foreground">
                {t('NoAdditionalErrorDetailsProvided')}
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>{t('Close')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
